/**
 * Avatar — one face, drawn the way the workspace draws faces.
 *
 * Same treatment as the account chip in the sidebar (`WorkspaceUserMenu`):
 * a round photo when there is one, otherwise the person's initials on the
 * muted surface. Shaped like shadcn's Avatar (root / image / fallback) so it
 * composes the way the rest of `components/ui` does, but with no dependency
 * of its own — the fallback is a span, not a primitive.
 *
 * @module components/ui/avatar
 */
import type { ComponentProps } from "react";

import { cn } from "~/lib/utils";

export function Avatar({ className, ...props }: ComponentProps<"span">) {
  return (
    <span
      data-slot="avatar"
      className={cn(
        "relative flex size-8 shrink-0 items-center justify-center overflow-hidden rounded-full bg-muted",
        className,
      )}
      {...props}
    />
  );
}

export function AvatarImage({ className, alt = "", ...props }: ComponentProps<"img">) {
  return (
    <img
      data-slot="avatar-image"
      alt={alt}
      referrerPolicy="no-referrer"
      className={cn("size-full rounded-full object-cover", className)}
      {...props}
    />
  );
}

export function AvatarFallback({ className, ...props }: ComponentProps<"span">) {
  return (
    <span
      data-slot="avatar-fallback"
      className={cn(
        "flex size-full items-center justify-center rounded-full font-semibold text-foreground",
        className,
      )}
      {...props}
    />
  );
}

/** Initials the way the account chip derives them: first letters, max two. */
export function personInitials(input: { name?: string | null; email?: string | null }): string {
  const source = input.name?.trim() || input.email?.trim() || "";
  // Only words that start with a letter or digit count: "Flawstick (dev)" is
  // "FD", not "F(".
  const parts = source
    .split(/[\s@._-]+/)
    .map((part) => part.replace(/^[^\p{L}\p{N}]+/u, ""))
    .filter((part) => part.length > 0);
  return parts.slice(0, 2).map((part) => part[0]?.toUpperCase() ?? "").join("") || "?";
}
